/**
 * ============================================================================
 * FORGOT PASSWORD - Password Reset Request Page
 * ============================================================================
 * Purpose: Allow users to request a password reset link via email
 * 
 * BACKEND INTEGRATION POINTS:
 * ----------------------------------------------------------------------------
 * 🔗 Backend: POST /api/v1/auth/forgot-password
 * Purpose: Send password reset email to user
 * Request Body: { email: string }
 * Success Response: { statusCode: 200, message: "Password reset link sent to email" }
 * Error Responses:
 *   - 404: User not found
 *   - 400: Invalid email format
 * 
 * IMPORTANT FOR BACKEND:
 * The email should contain a link in this format:
 * https://<frontend-domain>/reset-password/<resetToken>
 * Example: https://taskscheduler.com/reset-password/abc123xyz456
 * ============================================================================
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Loader from '../components/Loader';

const ForgotPassword = ({ isDarkMode }) => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [errors, setErrors] = useState({});

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors({});
    setMessage({ type: '', text: '' });

    // Validation
    const newErrors = {};
    if (!email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!validateEmail(email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsLoading(true);

    try {
      const API_URL = 'https://taskscheduler-backend.onrender.com/api/v1/auth/forgot-password';
      
      const response = await fetch(API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email: email.trim() })
      });

      const result = await response.json();

      if (response.ok && result.statusCode === 200) {
        setMessage({
          type: 'success',
          text: 'Password reset link sent to your email. Please check your inbox.'
        });
        setEmail('');
        
        // Redirect to login after 3 seconds
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      } else if (result.statusCode === 404) {
        setMessage({
          type: 'error',
          text: 'No account found with this email address.'
        });
      } else {
        setMessage({
          type: 'error',
          text: result.message || 'Failed to send reset link. Please try again.'
        });
      }
    } catch (error) {
      console.error('Error requesting password reset:', error);
      setMessage({
        type: 'error',
        text: 'Network error. Please check your connection and try again.'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const cardBg = isDarkMode
    ? 'linear-gradient(145deg, rgba(17, 24, 39, 0.95) 0%, rgba(31, 41, 55, 0.95) 100%)'
    : 'linear-gradient(145deg, #ffffff 0%, rgba(249, 250, 251, 1) 100%)';

  const inputBg = isDarkMode ? 'rgba(31, 41, 55, 0.5)' : '#ffffff';
  const inputBorder = isDarkMode ? 'rgba(75, 85, 99, 0.3)' : 'rgba(209, 213, 219, 0.5)';
  const textColor = isDarkMode ? 'rgba(255, 255, 255, 0.9)' : '#111827';
  const textSecondary = isDarkMode ? 'rgba(156, 163, 175, 0.9)' : '#6b7280';

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '2rem 1rem',
        background: isDarkMode
          ? 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)'
          : 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)'
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: '480px',
          background: cardBg,
          borderRadius: '1.5rem',
          boxShadow: isDarkMode
            ? '0 25px 50px -12px rgba(0, 0, 0, 0.7)'
            : '0 25px 50px -12px rgba(0, 0, 0, 0.15)',
          padding: window.innerWidth <= 480 ? '2rem 1.5rem' : '3rem 2.5rem',
          border: isDarkMode ? '1px solid rgba(75, 85, 99, 0.2)' : '1px solid rgba(229, 231, 235, 0.5)'
        }}
      >
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <h1
            style={{
              fontSize: window.innerWidth <= 480 ? '1.75rem' : '2rem',
              fontWeight: 800,
              color: textColor,
              marginBottom: '0.5rem'
            }}
          >
            Forgot Password?
          </h1>
          <p style={{ color: textSecondary, fontSize: '0.95rem', lineHeight: 1.6 }}>
            Enter your email address and we'll send you a link to reset your password.
          </p>
        </div>

        {/* Message Display */}
        {message.text && (
          <div
            style={{
              padding: '1rem 1.25rem',
              borderRadius: '0.85rem',
              marginBottom: '1.5rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem',
              background: message.type === 'success'
                ? 'linear-gradient(135deg, rgba(34, 197, 94, 0.1) 0%, rgba(22, 163, 74, 0.15) 100%)'
                : 'linear-gradient(135deg, rgba(239, 68, 68, 0.1) 0%, rgba(220, 38, 38, 0.15) 100%)',
              border: message.type === 'success'
                ? '1px solid rgba(34, 197, 94, 0.3)'
                : '1px solid rgba(239, 68, 68, 0.3)'
            }}
          >
            <i
              className={message.type === 'success' ? 'fas fa-check-circle' : 'fas fa-exclamation-circle'}
              style={{
                fontSize: '1.25rem',
                color: message.type === 'success' ? '#22c55e' : '#ef4444'
              }}
            ></i>
            <span style={{ color: message.type === 'success' ? '#22c55e' : '#ef4444', fontSize: '0.9rem', fontWeight: 600 }}>
              {message.text}
            </span>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '1.5rem' }}>
            <label
              htmlFor="email"
              style={{
                display: 'block',
                marginBottom: '0.5rem',
                color: textColor,
                fontWeight: 600,
                fontSize: '0.9rem'
              }}
            >
              Email Address
            </label>
            <div style={{ position: 'relative' }}>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                disabled={isLoading}
                style={{
                  width: '100%',
                  padding: '0.875rem 1rem',
                  borderRadius: '0.85rem',
                  border: errors.email
                    ? '2px solid #ef4444'
                    : `1px solid ${inputBorder}`,
                  background: inputBg,
                  color: textColor,
                  fontSize: '0.95rem',
                  outline: 'none',
                  transition: 'all 0.3s ease',
                  cursor: isLoading ? 'not-allowed' : 'text',
                  opacity: isLoading ? 0.6 : 1
                }}
              />
            </div>
            {errors.email && (
              <p style={{ color: '#ef4444', fontSize: '0.85rem', marginTop: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                <i className="fas fa-exclamation-circle"></i>
                {errors.email}
              </p>
            )}
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isLoading}
            style={{
              width: '100%',
              padding: '1rem',
              borderRadius: '0.85rem',
              border: 'none',
              background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
              color: '#ffffff',
              fontSize: '1rem',
              fontWeight: 700,
              cursor: isLoading ? 'not-allowed' : 'pointer',
              transition: 'all 0.3s ease',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.5rem',
              boxShadow: '0 10px 25px rgba(59, 130, 246, 0.3)',
              opacity: isLoading ? 0.7 : 1
            }}
            onMouseOver={(e) => {
              if (!isLoading) {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 15px 35px rgba(59, 130, 246, 0.4)';
              }
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 10px 25px rgba(59, 130, 246, 0.3)';
            }}
          >
            {isLoading ? (
              <Loader variant="inline" size="sm" text="Sending..." isDarkMode={isDarkMode} />
            ) : (
              <>
                <i className="fas fa-paper-plane"></i>
                Send Reset Link
              </>
            )}
          </button>
        </form>

        {/* Back to Login */}
        <div style={{ textAlign: 'center', marginTop: '2rem' }}>
          <button
            onClick={() => navigate('/login')}
            style={{
              background: 'none',
              border: 'none',
              color: '#3b82f6',
              fontSize: '0.9rem',
              fontWeight: 600,
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.5rem',
              transition: 'all 0.3s ease'
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.gap = '0.75rem';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.gap = '0.5rem';
            }}
          >
            <i className="fas fa-arrow-left"></i>
            Back to Login
          </button>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
